"use client";

import { useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "@/components/common/Link";
const eras = ["Luxury", "Boutique", "Resort", "Business", "Independent"];
const properties = [{
  id: 1,
  era: "Luxury Resort · 2023",
  price: "High Yield",
  name: "Grand Horizon Resort",
  address: "Coastal Region, India",
  specs: "200 ROOMS / 5 STAR / OTA OPTIMIZED",
  badge: "TOP PERFORMER",
  badgeType: "new",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_grand_horizon.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_grand_horizon.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_grand_horizon.png-wvc-sizes",
  notes: "A flagship property where we implemented dynamic pricing and OTA channel management, resulting in a 35% increase in RevPAR within the first six months."
}, {
  id: 2,
  era: "Boutique Hotel · 2022",
  price: "Growth Focus",
  name: "Heritage Inn Suites",
  address: "Historic District, India",
  specs: "45 ROOMS / 4 STAR / TECH INTEGRATED",
  badge: "DIRECT BOOKING",
  badgeType: "open",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_heritage_inn.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_heritage_inn.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_heritage_inn.png-wvc-sizes",
  notes: "Transformed the digital presence of this boutique property by integrating a modern booking engine and optimizing OTA listings for higher visibility."
}, {
  id: 3,
  era: "Business Hotel · 2024",
  price: "Maximized Rev",
  name: "Metro Business Hub",
  address: "Financial District, India",
  specs: "120 ROOMS / 3 STAR / DATA DRIVEN",
  badge: "",
  badgeType: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_metro_hub.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_metro_hub.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_metro_hub.png-wvc-sizes",
  notes: "Leveraged advanced revenue management algorithms to capture corporate demand and optimize weekday occupancy rates."
}, {
  id: 4,
  era: "Independent · 2023",
  price: "High Yield",
  name: "Mountain View Lodge",
  address: "Hill Station, India",
  specs: "30 ROOMS / 4 STAR / OTA STRATEGY",
  badge: "TOP PERFORMER",
  badgeType: "new",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_mountain_lodge.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_mountain_lodge.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_mountain_lodge.png-wvc-sizes",
  notes: "Successfully repositioned this independent lodge in the OTA landscape, driving significant growth in off-season bookings."
}, {
  id: 5,
  era: "Luxury Resort · 2024",
  price: "Growth Focus",
  name: "Azure Beach Villas",
  address: "Island Resort, India",
  specs: "60 ROOMS / 5 STAR / TECH STACK",
  badge: "DIRECT BOOKING",
  badgeType: "open",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_azure_villas.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_azure_villas.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_azure_villas.png-wvc-sizes",
  notes: "Implemented a comprehensive tech stack audit and OTA strategy to streamline operations and maximize direct revenue channels."
}, {
  id: 6,
  era: "Boutique · 2023",
  price: "Maximized Rev",
  name: "Urban Chic Hotel",
  address: "City Center, India",
  specs: "50 ROOMS / 4 STAR / REVENUE AUDIT",
  badge: "",
  badgeType: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_urban_chic.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_urban_chic.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_urban_chic.webp-wvc-sizes",
  notes: "Conducted a full revenue audit and implemented dynamic pricing strategies that stabilized occupancy and increased ADR."
}];
const carouselImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_lobby.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_lobby.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_lobby.png-wvc-sizes",
  alt: "Hotel lobby interior"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_suite.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_suite.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_suite.webp-wvc-sizes",
  alt: "Luxury hotel suite"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_dining.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_dining.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_interior_dining.png-wvc-sizes",
  alt: "Hotel dining area"
}];
export default function HotelPortfolioGallery() {
  const [selectedEras, setSelectedEras] = useState<string[]>([]);
  const [priceMax, setPriceMax] = useState(5000);
  const toggleEra = (era: string) => {
    setSelectedEras(prev => prev.includes(era) ? prev.filter(e => e !== era) : [...prev, era]);
  };
  const clearAll = () => {
    setSelectedEras([]);
    setPriceMax(5000);
  };
  return <section data-section-id="3335" id="hotelportfoliogallery" className="bg-background text-foreground font-default">
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7 md:col-start-2">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-secondary mb-6 md:mb-8">
              N° 01 — Success Stories
            </p>
            <h1 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              Our <em className="italic font-light">proven</em>
              <br />
              results.
            </h1>
          </div>

          <div className="col-span-12 md:col-span-3 md:col-start-10 flex flex-col gap-4 md:items-end md:text-right justify-end">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Showing 6 of 50+ Projects
            </p>
            <div className="flex items-center gap-2 md:justify-end">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Sort by:
              </span>
              <Select defaultValue="recent">
                <SelectTrigger size="sm" className="font-mono text-xs uppercase tracking-[0.1em] border-0 border-b border-border bg-transparent px-0 h-auto py-1 focus-visible:ring-0 hover:bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="font-mono text-xs">
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="impact">Highest Impact</SelectItem>
                  <SelectItem value="type">By Property Type</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-y border-border">
        <div className="px-6 md:px-12 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-center lg:h-[72px] py-4 lg:py-0 gap-4 lg:gap-0 overflow-x-auto">
            <div className="flex items-center gap-3 lg:pr-6 lg:border-r lg:border-border lg:h-full">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary shrink-0">
                Category
              </span>
              <div className="flex gap-2 flex-wrap">
                {eras.map((era, i) => <Button key={era} data-index={i} onClick={() => toggleEra(era)} variant="outline" size="sm" className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-md border h-7 px-3 transition-colors ${selectedEras.includes(era) ? "bg-primary text-white border-primary hover:bg-primary/90" : "border-border text-foreground hover:border-primary/40 hover:bg-transparent"}`}>
                    {era}
                  </Button>)}
              </div>
            </div>

            <div className="flex items-center gap-3 lg:px-6 lg:border-r lg:border-border lg:h-full">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary shrink-0">
                Revenue
              </span>
              <div className="flex items-center gap-3 min-w-[180px]">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground">
                  Low
                </span>
                <RangeInput min={500} max={10000} step={50} value={priceMax} onValueChange={setPriceMax} className="flex-1 h-1 bg-border accent-secondary" />
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground">
                  High
                </span>
              </div>
            </div>

            <div className="lg:ml-auto lg:pl-6">
              <Button variant="ghost" size="sm" onClick={clearAll} className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-primary hover:bg-transparent px-0 h-auto py-1">
                Clear all
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {properties.map((property, i) => <article key={property.id} data-index={i} className="group flex flex-col">
              <div className="relative overflow-hidden aspect-[4/5] bg-muted rounded-lg">
                <div className="absolute top-0 left-0 right-0 h-px bg-secondary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                <img src={property.image} data-wvc-srcset={property.srcset} data-wvc-sizes={property.sizes} alt={property.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                {property.badge && <Badge className={`absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.15em] rounded-md px-2.5 py-1 border-0 ${property.badgeType === "new" ? "bg-secondary text-white" : "bg-primary text-white"}`}>
                    {property.badge}
                  </Badge>}
              </div>

              <div className="flex items-baseline justify-between mt-6 gap-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-secondary">
                  {property.era}
                </span>
                <span className="font-default text-2xl text-foreground">
                  {property.price}
                </span>
              </div>

              <h3 className="font-default text-[22px] leading-tight mt-3 text-foreground">
                {property.name}
              </h3>

              <p className="text-sm text-muted-foreground mt-2 font-default">
                {property.address}
              </p>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-foreground/80">
                  {property.specs}
                </p>
              </div>

              <div className="flex items-center justify-between mt-5">
                <Link to="#" className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground border-b border-foreground/30 pb-0.5 hover:border-secondary transition-colors">
                  View Case Study →
                </Link>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-secondary hover:bg-transparent px-0 h-auto py-1 gap-1.5">
                      Quick View
                      <Plus className="size-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent showCloseButton={false} className="max-w-5xl w-[calc(100%-2rem)] p-0 bg-background border-border rounded-lg overflow-hidden gap-0">
                    <div className="grid grid-cols-1 md:grid-cols-5 max-h-[85vh]">
                      <div className="md:col-span-3 bg-muted relative">
                        <Carousel className="h-full">
                          <CarouselContent className="ml-0 h-full">
                            {carouselImages.map((img, idx) => <CarouselItem key={idx} data-index={idx} className="pl-0">
                                <div className="aspect-[4/5] md:aspect-auto md:h-[85vh] w-full">
                                  <img src={img.src} data-wvc-srcset={img.srcset} data-wvc-sizes={img.sizes} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </div>
                              </CarouselItem>)}
                          </CarouselContent>
                          <CarouselPrevious className="left-4 bg-background/90 border-border rounded-md size-9" />
                          <CarouselNext className="right-4 bg-background/90 border-border rounded-md size-9" />
                        </Carousel>
                      </div>

                      <div className="md:col-span-2 relative overflow-y-auto max-h-[85vh]">
                        <div className="absolute top-4 right-4 z-10">
                          <DialogClose asChild>
                            <Button variant="ghost" size="icon" className="size-8 rounded-md hover:bg-muted">
                              <X className="size-4" />
                              <span className="sr-only">Close</span>
                            </Button>
                          </DialogClose>
                        </div>
                        <div className="p-8 md:p-10">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary mb-3">
                            {property.era}
                          </p>
                          <h2 className="font-default text-3xl md:text-4xl leading-tight text-foreground">
                            {property.name}
                          </h2>
                          <p className="text-sm text-muted-foreground mt-2 font-default">
                            {property.address}
                          </p>
                          <p className="font-default text-3xl text-foreground mt-6">
                            {property.price}
                          </p>

                          <div className="mt-8 pt-6 border-t border-border grid grid-cols-2 gap-y-4 gap-x-6">
                            {[{
                          label: "Rooms",
                          value: "60"
                        }, {
                          label: "Rating",
                          value: "5 Star"
                        }, {
                          label: "Strategy",
                          value: "OTA Focus"
                        }, {
                          label: "Growth",
                          value: "35% RevPAR"
                        }, {
                          label: "Year",
                          value: "2024"
                        }, {
                          label: "Expert",
                          value: "Sunil Singh"
                        }].map((spec, idx) => <div key={spec.label} data-index={idx}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                  {spec.label}
                                </p>
                                <p className="font-default text-lg text-foreground mt-1">
                                  {spec.value}
                                </p>
                              </div>)}
                          </div>

                          <div className="mt-8 pt-6 border-t border-border">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary mb-3">
                              Revenue Strategy Notes
                            </p>
                            <p className="text-sm leading-relaxed text-foreground/80 font-default">
                              {property.notes}
                            </p>
                          </div>

                          <div className="mt-8 pt-6 border-t border-border">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary mb-2">
                              Consultant
                            </p>
                            <p className="font-default text-lg text-foreground">
                              Sunil Singh
                            </p>
                            <p className="text-sm text-muted-foreground font-default">
                              Revenue Management Expert
                            </p>
                          </div>

                          <div className="mt-8 flex flex-col gap-3">
                            <Button className="w-full rounded-md h-12 font-mono text-[11px] uppercase tracking-[0.2em] bg-primary text-white hover:bg-primary/90">
                              Book a Consultation
                            </Button>
                            <Button variant="ghost" className="w-full rounded-md h-12 font-mono text-[11px] uppercase tracking-[0.2em] border border-border hover:bg-muted">
                              View All Services
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </article>)}
        </div>

        <div className="mt-20 md:mt-24 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationPrevious href="#" className="font-mono text-[11px] uppercase tracking-[0.18em] rounded-md border-0 hover:bg-transparent hover:text-secondary text-muted-foreground gap-1 px-2" />
              </PaginationItem>
              {["01", "02", "03", "04"].map((page, i) => <PaginationItem key={page} data-index={i}>
                  <PaginationLink href="#" isActive={i === 0} className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-md border-0 size-9 ${i === 0 ? "text-secondary bg-transparent" : "text-muted-foreground hover:text-secondary hover:bg-transparent"}`}>
                    {page}
                  </PaginationLink>
                </PaginationItem>)}
              <PaginationItem>
                <PaginationNext href="#" className="font-mono text-[11px] uppercase tracking-[0.18em] rounded-md border-0 hover:bg-transparent hover:text-secondary text-foreground gap-1 px-2" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </section>;
}